import React, { useCallback, useEffect, useRef, useState } from 'react';

export const AUTO_SCROLL_THRESHOLD = 40;

type Params = {
  streamEndRef: React.RefObject<HTMLDivElement>;
  activeRequestId: any;
  isStreaming: boolean;
  contentVersion: string | number;
  showResponseArea: boolean;
};

type JumpBehavior = 'auto' | 'smooth';

export function useAutoScroll({
  streamEndRef,
  activeRequestId,
  isStreaming,
  contentVersion,
  showResponseArea,
}: Params) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const lastJumpedRequestIdRef = useRef<any>(null);

  const updateJumpVisibility = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || !showResponseArea) {
      setShowJumpToLatest(false);
      return;
    }

    const distanceToBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    setShowJumpToLatest(distanceToBottom > AUTO_SCROLL_THRESHOLD);
  }, [showResponseArea]);

  const jumpToLatest = useCallback(
    (behavior: JumpBehavior = 'smooth') => {
      const container = scrollContainerRef.current;
      if (container) {
        container.scrollTo({ top: container.scrollHeight, behavior });
        requestAnimationFrame(updateJumpVisibility);
        return;
      }

      streamEndRef.current?.scrollIntoView({ behavior, block: 'end' });
    },
    [streamEndRef, updateJumpVisibility],
  );

  const handleScroll = useCallback(() => {
    updateJumpVisibility();
  }, [updateJumpVisibility]);

  useEffect(() => {
    const rafId = requestAnimationFrame(updateJumpVisibility);
    return () => cancelAnimationFrame(rafId);
  }, [contentVersion, showResponseArea, updateJumpVisibility]);

  useEffect(() => {
    if (!activeRequestId || !isStreaming) return;
    if (lastJumpedRequestIdRef.current === activeRequestId) return;

    lastJumpedRequestIdRef.current = activeRequestId;
    const rafId = requestAnimationFrame(() => {
      jumpToLatest('auto');
    });
    return () => cancelAnimationFrame(rafId);
  }, [activeRequestId, isStreaming, jumpToLatest]);

  return {
    scrollContainerRef,
    showJumpToLatest,
    handleScroll,
    jumpToLatest,
  };
}
